import React from 'react';
import { Link } from 'react-router-dom';

/* ── Integration data ────────────────────────────────────────── */
const integrations = [
  {
    name: 'PCRecruiter',
    desc: 'Full-featured ATS and CRM for staffing and recruiting firms. Push sourced contacts directly into candidate records and sync reporting live.',
    type: 'ATS / CRM',
    badge: 'ats',
  },
  {
    name: 'Crelate',
    desc: 'Modern recruiting CRM with talent relationship management. Contacts sync automatically with real-time sourcing and pipeline reporting.',
    type: 'ATS / CRM',
    badge: 'ats',
  },
  {
    name: 'Stardex',
    desc: 'Recruiting and staffing platform with advanced search and match. Sourced data flows directly in — no mapping, no manual exports.',
    type: 'ATS',
    badge: 'ats',
  },
  {
    name: 'BigBiller / TE Recruit',
    desc: 'Cloud recruiting software by Top Echelon built for staffing agencies. Contacts and sourcing activity sync seamlessly into your workflows.',
    type: 'ATS',
    badge: 'ats',
  },
  {
    name: 'Monday.com',
    desc: 'Work management platform for organizing recruiting and sales workflows. Live sourcing data and contact tracking surface directly in your boards.',
    type: 'Productivity',
    badge: 'productivity',
  },
  {
    name: 'HubSpot',
    desc: 'Leading CRM for BD contacts, outreach sequences, and pipelines. Sourced contacts push into HubSpot with enrichment data attached.',
    type: 'CRM',
    badge: 'crm',
  },
  {
    name: 'RingCentral',
    desc: 'Cloud communications for calling and texting candidates. Connect your kNine contacts directly to your RingCentral workspace.',
    type: 'Communications',
    badge: 'comm',
  },
  {
    name: 'Kruiterly',
    desc: 'Consulting and advisory for custom integrations and websites. Expert guidance on connecting kNine into your existing tech stack.',
    type: 'Consulting',
    badge: 'crm',
  },
];

/* ── Badge colors for initials ──────────────────────────────── */
const badgeColors = {
  ats: '#1565C0',
  crm: '#7c3aed',
  comm: '#d97706',
  productivity: '#059669',
};

/* ── Page component ──────────────────────────────────────────── */
const Integrations = () => {
  return (
    <>
      {/* Header */}
      <section className="hero" style={{ paddingBottom: '40px' }}>
        <div className="container hero-content fade-up">
          <div className="section-badge blue">🔗 Integrations</div>
          <h1 className="hero-title" style={{ fontSize: '2.75rem' }}>
            Built-in <span className="text-gradient">Integrations</span>
          </h1>
          <p className="hero-subtitle">
            Our team builds and maintains every integration. Just connect your account and
            sourcing &amp; reporting data start flowing live — no mapping, no configuration needed.
          </p>
        </div>
      </section>

      {/* Ready-to-connect callout */}
      <section style={{ paddingTop: 0, paddingBottom: '8px' }}>
        <div className="container">
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
            <span className="tag tag-blue">All Integrations</span>
            <span className="tag" style={{ background: '#EFF6FF', color: 'var(--primary)' }}>ATS</span>
            <span className="tag" style={{ background: '#f3e8ff', color: '#7c3aed' }}>CRM</span>
            <span className="tag" style={{ background: '#fef3c7', color: '#d97706' }}>Communications</span>
            <span className="tag" style={{ background: '#d1fae5', color: '#059669' }}>Productivity</span>
          </div>
          <p style={{ textAlign: 'center', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            All listed integrations are <strong>built by our team</strong> and ready to connect today.
          </p>
        </div>
      </section>

      {/* Integration Grid */}
      <section style={{ paddingTop: '24px' }}>
        <div className="container">
          <div className="integration-grid">
            {integrations.map((int) => (
              <div key={int.name} className="integration-card">
                <div
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    background: badgeColors[int.badge] || 'var(--primary)',
                    color: 'white',
                    fontWeight: 800,
                    fontSize: '1.25rem',
                    letterSpacing: '-0.5px',
                  }}
                >
                  {int.name.charAt(0)}
                </div>
                <div className="integration-name">{int.name}</div>
                <div className="integration-desc">{int.desc}</div>
                <span className={`integration-badge ${int.badge}`}>{int.type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PCRecruiter Spotlight */}
      <section className="bg-off-white" style={{ paddingTop: '48px', paddingBottom: '48px' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', alignItems: 'center' }}>
            <div>
              <div className="section-badge blue">Featured Integration</div>
              <h2 className="section-title" style={{ fontSize: '1.5rem', marginBottom: '12px' }}>
                kNine + <span className="text-gradient">PCRecruiter</span>
              </h2>
              <p style={{ color: 'var(--text)', lineHeight: 1.8, marginBottom: '16px', fontSize: '0.95rem' }}>
                kNine integrates with PCRecruiter to help recruiting teams source candidates and
                companies outside their existing ATS database while maintaining their existing workflow.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px' }}>
                {[
                  'Push sourced contacts directly into PCRecruiter',
                  'Pre-configured field mapping — zero setup',
                  'Verified emails and phone numbers included',
                  'Works for both recruiting and BD sourcing',
                ].map((item) => (
                  <li key={item} style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '8px', fontSize: '0.9rem', color: 'var(--text)' }}>
                    <span className="check-icon" style={{ fontSize: '0.9rem' }}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/pcrecruiter-sourcing" className="btn btn-primary">
                Learn More
              </Link>
            </div>
            <div className="card-flat" style={{ padding: '32px', textAlign: 'center' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                background: '#1565C0',
                color: 'white',
                fontWeight: 800,
                fontSize: '1.5rem',
              }}>
                P
              </div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px' }}>
                PCRecruiter
              </h3>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                Full-featured ATS and CRM for staffing and recruiting firms
              </p>
              <div style={{ fontSize: '0.8rem', color: 'var(--primary)', fontWeight: 600, marginTop: '12px' }}>
                Native Integration
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Integrations Work */}
      <section className="bg-off-white">
        <div className="container">
          <div className="text-center" style={{ marginBottom: '48px' }}>
            <h2 className="section-title" style={{ fontSize: '1.75rem' }}>
              Up and Running in <span className="text-gradient">Minutes</span>
            </h2>
            <p className="section-subtitle mx-auto">
              Our team handles all configuration before you even connect. There's nothing to set up on your end.
            </p>
          </div>

          {/* No-mapping banner */}
          <div style={{
            background: 'var(--primary-bg)',
            border: '1px solid #BBDEFB',
            borderRadius: 'var(--radius-lg)',
            padding: '20px 28px',
            maxWidth: '720px',
            margin: '0 auto 48px',
            display: 'flex',
            gap: '16px',
            alignItems: 'flex-start',
          }}>
            <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>🔧</span>
            <div>
              <div style={{ fontWeight: 700, color: 'var(--primary-dark)', marginBottom: '4px', fontSize: '0.95rem' }}>
                Zero configuration on your end
              </div>
              <div style={{ color: 'var(--primary)', fontSize: '0.875rem', lineHeight: 1.6 }}>
                Field mapping, workflow rules, and data schemas are all pre-configured by the kNine team before you connect.
                You never touch a settings panel — just authorize and start sourcing.
              </div>
            </div>
          </div>

          <div className="feature-grid-2" style={{ maxWidth: '720px', margin: '0 auto' }}>
            {[
              {
                step: '1',
                icon: '🔑',
                title: 'Connect Your Account',
                desc: 'Authorize kNine to connect with your ATS, CRM, or platform through secure OAuth or API key. Takes under a minute.',
              },
              {
                step: '2',
                icon: '🚀',
                title: 'Start Syncing Live',
                desc: 'Push sourced contacts with one click or set up automatic sync. Sourcing activity and reporting flow into your platform in real-time.',
              },
            ].map((item) => (
              <div key={item.step} className="card-flat" style={{ textAlign: 'center', padding: '36px 24px' }}>
                <div style={{ fontSize: '2rem', marginBottom: '12px' }}>{item.icon}</div>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: 'var(--primary)',
                  color: 'white',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  marginBottom: '12px',
                }}>
                  {item.step}
                </div>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px' }}>
                  {item.title}
                </h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', lineHeight: 1.7 }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Integration Section */}
      <section>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', alignItems: 'center', maxWidth: '960px', margin: '0 auto' }}>
            <div>
              <div className="section-badge purple">Custom Integrations</div>
              <h2 className="section-title" style={{ fontSize: '1.75rem' }}>
                Need Something <span className="text-gradient">Custom?</span>
              </h2>
              <p style={{ color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: '24px' }}>
                We don't offer an open API, but our team can build a custom integration with
                virtually any platform. If your tool isn't listed, reach out — we'll scope it
                and have it connected for you.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  'Custom-built integrations by our team',
                  'Tailored to your specific workflow',
                  'Full data sync and reporting',
                  'Ongoing maintenance and support included',
                ].map((item) => (
                  <li key={item} style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px', fontSize: '0.9rem', color: 'var(--text)' }}>
                    <span className="check-icon" style={{ fontSize: '0.9rem' }}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <div style={{ marginTop: '24px' }}>
                <Link to="/contact" className="btn btn-primary btn-lg">
                  Request a Custom Integration
                </Link>
              </div>
            </div>
            <div className="card-flat" style={{ padding: '36px', textAlign: 'center' }}>
              <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🔧</div>
              <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '12px' }}>
                How It Works
              </h3>
              <div style={{ textAlign: 'left' }}>
                {[
                  { step: '1', text: 'Tell us which platform you need connected' },
                  { step: '2', text: 'Our team scopes the integration and timeline' },
                  { step: '3', text: 'We build, test, and deploy it for you' },
                  { step: '4', text: 'You connect and start syncing — zero config' },
                ].map((item) => (
                  <div key={item.step} style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '14px' }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: 'var(--primary)',
                      color: 'white',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      {item.step}
                    </div>
                    <span style={{ fontSize: '0.9rem', color: 'var(--text)' }}>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="container">
          <div className="cta-section">
            <div className="cta-paw" style={{ top: '-20px', left: '-10px', transform: 'rotate(-25deg)' }}>🐾</div>
            <div className="cta-paw" style={{ bottom: '-20px', right: '-10px', transform: 'rotate(15deg)' }}>🐾</div>
            <h2>Don't See Your Tool?</h2>
            <p>
              We build integrations fast. Tell us what you use and our team will get it connected.
            </p>
            <Link to="/contact" className="btn btn-white btn-lg" style={{ border: 'none' }}>
              Request an Integration
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Integrations;
